import { Box, Flex } from "@chakra-ui/react"
import { FormattedMessage } from "react-intl"

import FormattedDate from "@/components/FormattedDate"
import { P4 } from "@/components/Typographies"
import type { SharedDataJsonldReadCollectionSharedData } from "@/networking/api"

import { InterBUStatusTag } from "../InterBUStatus/InterBUStatusTag"
import { TIMELINE_STEP_COLORS } from "../InterBUStatus/interbuStatusPalette"
import { getTimeline } from "./cardRules"

// Three steps of a share (ADR 0009, D3): a done step is a dot filled with its colour (DS InterBU
// palette: sent, accepted, completed), a step to come a hollow one. The line that leads to a done
// step keeps the colour of the step before it. A refusal, revocation, cancellation or abandonment
// stops the timeline with its date. On a narrow card the steps wrap whole onto the next line
// rather than overflowing it.
export const InterBUTimeline: React.FC<{ share: SharedDataJsonldReadCollectionSharedData }> = ({
  share,
}) => {
  const { steps, stop } = getTimeline(share)

  return (
    <Flex alignItems="center" gap="3" wrap="wrap" data-testid="interbu-timeline">
      <Flex
        as="ol"
        listStyleType="none"
        alignItems="center"
        wrap="wrap"
        columnGap="2"
        rowGap="1"
        minW={0}
        data-testid="interbu-timeline-steps"
      >
        {steps.map((step, index) => {
          const color = TIMELINE_STEP_COLORS[step.key]?.fill ?? "text"
          const previous = index > 0 ? steps[index - 1] : undefined
          const lineColor = previous && TIMELINE_STEP_COLORS[previous.key]?.fill
          return (
            <Flex as="li" key={step.key} alignItems="center" gap="2" data-done={step.isDone}>
              {index > 0 && (
                <Box
                  w="6"
                  h="2px"
                  flexShrink={0}
                  bg={step.isDone ? (lineColor ?? "text") : "lineStrong"}
                  data-testid="interbu-timeline-line"
                />
              )}
              <Box
                boxSize="2.5"
                flexShrink={0}
                borderRadius="full"
                border="1.5px solid"
                borderColor={step.isDone ? color : "lineStrong"}
                bg={step.isDone ? color : "transparent"}
                data-testid="interbu-timeline-dot"
                aria-hidden
              />
              <P4 color={step.isDone ? "text" : "textMuted"} whiteSpace="nowrap">
                <FormattedMessage id={`sharing.interbu.card.timeline.${step.key}`} />
                {step.isDone && step.date && (
                  <>
                    {" "}
                    <FormattedDate date={step.date} />
                  </>
                )}
              </P4>
            </Flex>
          )
        })}
      </Flex>
      {stop && (
        <Flex alignItems="center" gap="2" wrap="wrap" data-testid="interbu-timeline-stop">
          <InterBUStatusTag status={stop.status} />
          {stop.date && (
            <P4 color="textMuted">
              <FormattedDate date={stop.date} />
            </P4>
          )}
        </Flex>
      )}
    </Flex>
  )
}
